import {
  BlockVariant,
  ButtonBlockProps,
  CommentBlockProps,
  DynamicBlock,
  DynamicBlocks,
  InputTextareaBlockProps,
  TextBlockProps,
} from '@/components/block-builder/block-builder.types';
import { FileService } from '@/components/shared/file-list/file-list.service';
import { DacDetails } from '@/types';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import _ from 'lodash';

interface getInspectDacBlocksProps {
  refreshCount: number;
  status?: string;
  version?: number;
  downloadSignedAAOModel?: () => void;
  downloadSignedDACModel?: () => void;
  accessHistory?: () => void;
}

interface getDraftDacBlocksProps {
  save?: () => void;
  disabledSave?: Observable<boolean>;
  disableValidate?: Observable<boolean>;
  rest?: () => void;
  globalPreview?: () => void;
  openEditDynamicDocsCopySheet?: () => void;
  validate?: () => void;
  loading?: BehaviorSubject<boolean>;
}

interface getFlowableDacBlocksProps {
  refreshCount: number;
  task: DacDetails;
  save?: () => void;
  saveDates?: () => void;
  validate?: (value?: string) => void;
  specialValidate?: (value?: string) => void;
  reject?: () => void;
  //ac validation **********************
  openDownloadAaoDialog?: () => void;
  downloadModelDac?: () => void;
  openUploadDialog?: () => void;
  // **********************************
  globalPreview?: () => void;
  comment?: BehaviorSubject<string>;
  onCommentChange?: (value: string) => void;
  error?: BehaviorSubject<string | null>;
  accessHistory?: () => void;
  openEditDynamicDocsCopySheet?: () => void;
}

export enum TaskVariant {
  VALIDATION = 'Validation (AC)',
  VALIDATION_CPMP = 'Validation DAC (CPMP)',
  PUBLICATION = '(AC) Publication',
  RECTIFICATION = 'Rectification (AC)',
  RESERVED = 'Réserve (CPMP)',
  AVIS_SOUS_COMMISSION_SPECIAL = '(CNCMP) Avis sous-commission spécialisée',
  AFFECTAION_COMMISSION_PERMANENTE = '(CNCMP) commission permanente Affectation',
  DECISION_COMMISSION_PERMANENTE = '(CNCMP) Décision commission permanente',
  REVISION_COMMISSION_SPECIAL = '(CNCMP) Révision sous-commission spécialisée',
  FILTRAGE_ET_QUALIFICATION_DOSSIER = '(CPMP) Filtrage et qualification dossier',
  AFFECTATION_COMMISSION_SPECIALISEE = '(CNCMP) commission spécialisée Affectation',
  AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE = '(CNCMP) Avis + Note Rapporteur commission spécialisée',
  FILTRAGE_ET_QUALIFICATION_DOSSIER_CNCMP = '(CNCMP) Filtrage et qualification dossier',
  CONFIRMATION_DES_DATES = '(AC) Confirmation des Dates',
}

@Injectable({
  providedIn: 'root',
})
export class DacWorkflowBlocksService {
  fileService = inject(FileService);
  static mapToFrenchValues(value: string) {
    if (value == 'Validate') return 'Valider';
    if (value == 'Reject') return 'Rejeter';
    if (value == 'Publish') return 'Publier';
    if (value == 'Assign') return 'Affecter';
    return value;
  }

  accessHistoryButton = (accessHistory?: () => void): DynamicBlock => {
    return {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'dac.actions.access_history',
        onClick: accessHistory,
        severity: 'secondary',
      },
    };
  };

  refreshedLabel = (refreshCount: number): DynamicBlock<TextBlockProps> => {
    if (refreshCount === 0)
      return {
        variant: BlockVariant.CUSTOM,
      };
    else
      return {
        variant: BlockVariant.TEXT,
        props: {
          blocks: [
            {
              content: refreshCount === 1 ? 'Ce document a été actualisé une seule fois' : `Ce document a été actualisé ${refreshCount} fois`,
              class: 'text-[1rem] font-thin opacity-70 no-select italic',
            },
          ],
          orientation: 'horizontal',
          gap: 1,
        },
        class: 'w-fit mx-auto',
      };
  };

  getInspectDacBlocks = ({
    refreshCount,
    downloadSignedAAOModel,
    downloadSignedDACModel,
    accessHistory,
  }: getInspectDacBlocksProps): DynamicBlocks[] => {
    const finalBlocks: DynamicBlocks[] = [];

    const downloadSignedAAOModelButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'dac.actions.download_model_aao',
        icon: 'pi pi-download',
        onClick: () => downloadSignedAAOModel?.(),
        severity: 'info',
      },
    };

    const downloadSignedDACModelButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'dac.actions.download_model_dac',
        icon: 'pi pi-download',
        onClick: () => downloadSignedDACModel?.(),
        severity: 'info',
      },
    };

    finalBlocks.push({});

    if (downloadSignedAAOModel) finalBlocks[0]['1'] = [downloadSignedAAOModelButton];
    if (downloadSignedDACModel) {
      finalBlocks[0]['2'] = [downloadSignedDACModelButton];
    }
    if (_.isEqual(finalBlocks[0], {})) finalBlocks.splice(0, 1);

    finalBlocks.push({
      '0': [this.accessHistoryButton(accessHistory)],
      '1': [this.refreshedLabel(refreshCount)],
    });

    return finalBlocks;
  };

  getDraftDacBlocks = ({
    save,
    disabledSave,
    disableValidate,
    rest,
    globalPreview,
    openEditDynamicDocsCopySheet,
    validate,
    loading,
  }: getDraftDacBlocksProps): DynamicBlocks[] => {
    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
        loading: loading,
        severity: 'success',
        disabled: disabledSave,
      },
    };
    const resetButton: DynamicBlock = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Réinitialiser',
        onClick: rest,
        severity: 'secondary',
      },
    };

    const previewButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Prévisualiser',
        onClick: () => globalPreview?.(),
        severity: 'info',
      },
    };

    const validateButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Passer pour validation',
        onClick: () => validate?.(),
        severity: 'success',
        disabled: disableValidate,
      },
    };

    const editDynamicDocsCopyButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Modifier les documents dynamiques',
        onClick: () => openEditDynamicDocsCopySheet?.(),
        severity: 'contrast',
        icon: 'pi pi-pencil',
      },
    };

    return [
      {
        '0': [saveButton],
        '1': [resetButton],
        '2': [validateButton],
      },
      {
        '0': [previewButton],
        '1': [editDynamicDocsCopyButton],
      },
    ];
  };

  getFlowableDacBlocks = ({
    refreshCount,
    task,
    save,
    saveDates,
    validate,
    specialValidate,
    openDownloadAaoDialog,
    downloadModelDac,
    openUploadDialog,
    reject,
    comment,
    onCommentChange,
    error,
    accessHistory,
    globalPreview,
    openEditDynamicDocsCopySheet,
  }: getFlowableDacBlocksProps): DynamicBlocks[] => {
    const blocks: DynamicBlock[] = [];
    let renderTextarea = false;

    const saveButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Enregistrer',
        onClick: () => save?.(),
        severity: 'success',
      },
    };
    if (save && task.isUpdatable) blocks.push(saveButton);

    //detect status
    if (task?.taskName == TaskVariant.VALIDATION) {
      //detect actions
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          if (`${step.validationValue}` == 'true') {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Télécharger AAO`,
                severity: 'success',
                icon: 'pi pi-download',
                onClick: () => openDownloadAaoDialog?.(),
              },
            });
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Télécharger DAC`,
                severity: 'success',
                icon: 'pi pi-download',
                onClick: () => downloadModelDac?.(),
              },
            });
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: `Déposer`,
                severity: 'secondary',
                icon: 'pi pi-download rotate-180',
                onClick: () => openUploadDialog?.(),
              },
            });
          } else {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
                icon: 'pi pi-times',
                onClick: () => reject?.(),
              },
            });
          }
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.PUBLICATION) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: 'Publier',
          onClick: () => specialValidate?.(),
        },
      });
    } else if (task?.taskName == TaskVariant.RECTIFICATION) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => validate?.(),
            },
          });
        });
    } else if (task?.taskName == TaskVariant.RESERVED) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AVIS_SOUS_COMMISSION_SPECIAL) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AFFECTAION_COMMISSION_PERMANENTE) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue === 'true' ? specialValidate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.DECISION_COMMISSION_PERMANENTE) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.REVISION_COMMISSION_SPECIAL) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.FILTRAGE_ET_QUALIFICATION_DOSSIER) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AFFECTATION_COMMISSION_SPECIALISEE) {
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: DacWorkflowBlocksService.mapToFrenchValues('Assign'),
          onClick: () => specialValidate?.(),
        },
      });
      blocks.push({
        variant: BlockVariant.BUTTON,
        props: {
          label: DacWorkflowBlocksService.mapToFrenchValues('Reject'),
          onClick: () => reject?.(),
        },
      });

      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.AVIS_NOTE_RAPPORTEUR_COMMISSION_SPECIALISEE) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => specialValidate?.('true'),
            },
          });
        });
    } else if (task?.taskName == TaskVariant.FILTRAGE_ET_QUALIFICATION_DOSSIER_CNCMP) {
      task?.nextSteps
        ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
        .forEach(step => {
          blocks.push({
            variant: BlockVariant.BUTTON,
            props: {
              label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
              onClick: () => (step.validationValue ? validate?.(step.validationValue) : reject?.()),
            },
          });
        });
      renderTextarea = true;
    } else if (task?.taskName == TaskVariant.CONFIRMATION_DES_DATES) {
      console.log(task.dateRequired, task.dacDocs.dateLimiteRecptionDemande, task.dacDocs.dateLimiteSoumission);
      if (task.dateRequired && task.dacDocs.dateLimiteRecptionDemande.length > 0 && task.dacDocs.dateLimiteSoumission.length > 0 && task.dacDocs.dateOuverture.length > 0) {
        blocks.push({
          variant: BlockVariant.BUTTON,
          props: {
            label: `Télécharger AAO`,
            severity: 'success',
            icon: 'pi pi-download',
            onClick: () => openDownloadAaoDialog?.(),
          },
        });
        blocks.push({
          variant: BlockVariant.BUTTON,
          props: {
            label: `Télécharger DAC`,
            severity: 'success',
            icon: 'pi pi-download',
            onClick: () => downloadModelDac?.(),
          },
        });
        task?.nextSteps
          ?.sort((a, b) => (a.validationValue > b.validationValue ? -1 : 1))
          .forEach(step => {
            blocks.push({
              variant: BlockVariant.BUTTON,
              props: {
                label: DacWorkflowBlocksService.mapToFrenchValues(step.actionLabel),
                onClick: () => specialValidate?.('true'),
                severity: 'secondary',
              } satisfies ButtonBlockProps,
            });
          });
      } else {
        blocks.push({
          variant: BlockVariant.BUTTON,
          props: {
            label: 'Enregistrer',
            onClick: () => saveDates?.(),
            severity: 'success',
          },
        });
      }
    }

    const textareablock: DynamicBlock<InputTextareaBlockProps> = {
      variant: BlockVariant.INPUT_TEXTAREA,
      props: {
        label: 'Commentaire',
        rows: 5,
        value: comment?.getValue(),
        placeholder: 'Saisissez un commentaire',
        onChange: (value: string) => onCommentChange?.(value),
        error: error,
      },
    };

    const commentBlock: DynamicBlock<CommentBlockProps> = {
      variant: BlockVariant.COMMENT,
      class: 'w-full',
      props: {
        label: 'Commentaire',
        content: task?.traceability?.comment || '',
        date: task?.traceability?.dateComment || '',
        class: 'w-full border p-2 rounded-md',
      },
    };

    const meetingRequiredBlock: DynamicBlock<TextBlockProps> = {
      variant: BlockVariant.TEXT,
      props: {
        gap: 1,
        blocks: [
          {
            content: 'Ce dossier ne peut pas être traité en dehors d’une réunion, il est nécessaire d’en planifier une.',
            class: 'text-sm italic opacity-70',
          },
        ],
      },
    };

    const finalBlocks: DynamicBlocks = {};
    if (task?.traceability?.comment) finalBlocks['0'] = [commentBlock];
    if (blocks.length > 0 && !task?.meetingRequired) finalBlocks['1'] = blocks;
    if (renderTextarea && !task?.meetingRequired) finalBlocks['2'] = [textareablock];
    if (task?.meetingRequired) finalBlocks['3'] = [meetingRequiredBlock];

    const previewButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Prévisualiser',
        onClick: () => globalPreview?.(),
        severity: 'info',
      },
    };

    const editDynamicDocsCopyButton: DynamicBlock<ButtonBlockProps> = {
      variant: BlockVariant.BUTTON,
      props: {
        label: 'Modifier les documents dynamiques',
        onClick: () => openEditDynamicDocsCopySheet?.(),
        severity: 'contrast',
        icon: 'pi pi-pencil',
      },
    };

    const secondaryBlocks: DynamicBlocks = {
      '0': [this.accessHistoryButton(accessHistory)],
      '1': [this.refreshedLabel(refreshCount)],
    };
    if (openEditDynamicDocsCopySheet) secondaryBlocks['2'] = [editDynamicDocsCopyButton];
    if (globalPreview) secondaryBlocks['3'] = [previewButton];

    console.log([finalBlocks, secondaryBlocks]);

    return [finalBlocks, secondaryBlocks].filter(b => Object.keys(b).length > 0);
  };
}
